import { Footer } from "@/components/Footer";
import { PageMeta } from "@/components/site/blocks";
import { Band, Closing, Faqs, Hero, In, List, Related, Steps, WhatIs, WorkflowCard } from "@/components/seo/kit";

// /for-founders: segment page. Rewritten 2026-09-29 for today's product
// (SEO run: gtm/products/clearmakes-inbox/seo/). Examples are illustrations, not customers.

const PATH = "/for-founders";

const faqs = [
  {
    q: "Will it write my emails for me?",
    a: "It drafts replies for the routine ones you choose, such as enquiries and common questions, and you check them. The emails that need you, such as investors, hiring and hard customer conversations, stay with you.",
  },
  {
    q: "What do I need for the first workflow?",
    a: "You connect your mailbox and one other app, type one sentence, and check the steps. Inbox asks at most one question.",
  },
  {
    q: "What does it cost for a founder?",
    a: "Free covers 3 apps, 2 new workflows a month and 100 runs a month. Most founders who rely on it will want Starter at $29 a month (5,000 runs) or Growth at $59 a month with no limits.",
  },
];

const ForFounders = () => (
  <>
    <PageMeta
      title="Email Automation for Founders | ClearMakes Inbox"
      description="For founders who run the company inbox: send enquiries to HubSpot, invoices to finance and common questions to saved answers, set up in one sentence."
      path={PATH}
      crumbs={[
        { name: "Home", path: "/" },
        { name: "Solutions", path: "/solutions" },
        { name: "Founders", path: PATH },
      ]}
    />
    <main>
      <Hero
        eyebrow="Solutions · Founders"
        title="Email automation for founders who still run the inbox"
        lead="You are the sales team, the support desk and the accounts department. Hand the routine emails to workflows and keep your time for the ones only you can answer."
        secondary={{ label: "All solutions", href: "/solutions" }}
      />

      <WhatIs title="What does ClearMakes Inbox do for a founder?">
        For a founder, ClearMakes Inbox takes the routine steps out of the company inbox. Enquiries go to HubSpot with a drafted reply, invoices
        go to whoever pays them, and the questions you answer every week get your saved answer. You describe each workflow in one sentence.
        Free to start; paid plans from $29 a month.
      </WhatIs>

      <Band tone="pale" title="Three workflows founders start with" answer="Start with the emails you handle the same way every time. These three cover sales, money and repeat questions.">
        <div className="grid gap-5 lg:grid-cols-3">
          <WorkflowCard
            sentence="When someone asks for a demo, add them to HubSpot and draft a reply with my booking link."
            steps={["When an email arrives in Gmail", "Check it asks for a demo", "Create the contact in HubSpot", "Draft a reply"]}
          />
          <WorkflowCard
            sentence="When a supplier emails an invoice, post the amount and due date to #finance."
            steps={["When an email arrives in Gmail", "Stop if it isn't an invoice", "Pull out the details: amount, due date", "Post to #finance in Slack"]}
          />
          <WorkflowCard
            sentence="When an investor or board member emails, create a task for me in Notion."
            steps={["When an email arrives in Gmail", "Check it is from an investor or board member", "Add a page in Notion"]}
          />
        </div>
      </Band>

      <Band tone="white" title="How to hand over your first routine email" answer="Pick the email you answer the same way most often. Automate that one, watch it for a week, then add the next.">
        <Steps
          items={[
            { title: "Connect Gmail or Outlook and one other app", body: <>From the Apps page in <In href="/products/inbox">ClearMakes Inbox</In>.</> },
            { title: "Describe the workflow in one sentence", body: "Say which emails and what should happen. Start with drafts, not automatic sending." },
            { title: "Save your three most common answers", body: <>In <In href="/smart-faq">Smart FAQ</In>, so common questions get your exact words.</> },
            { title: "Read the runs for a week", body: "Each run lists its steps. Fix the sentence if anything looks off." },
          ]}
        />
      </Band>

      <Band tone="pale" title="Who this is not for" answer="If you already have an assistant who handles the inbox, or you want an AI that writes all your email, Inbox isn't that. It does the routine steps you describe." narrow>
        <List
          items={[
            "Not an email app: you still read and write email in Gmail or Outlook.",
            "Not a replacement for judgement: investor, hiring and customer-escalation emails should stay with you.",
            "AI steps can misread now and then; prefer drafts for anything that matters.",
          ]}
        />
      </Band>

      <Faqs items={faqs} />

      <Related
        links={[
          { href: "/lead-response-automation", label: "Lead response automation", desc: "Reply to new enquiries in minutes." },
          { href: "/integrations/email-to-hubspot", label: "Email leads to HubSpot", desc: "Contacts with the details filled in." },
          { href: "/smart-faq", label: "Smart FAQ", desc: "Saved answers to everyday questions." },
          { href: "/for-freelancers", label: "For freelancers", desc: "Client email, handled." },
          { href: "/solutions", label: "All solutions", desc: "ClearMakes Inbox by team." },
          { href: "/pricing", label: "Pricing", desc: "Free, Starter, Growth and Enterprise." },
        ]}
      />

      <Closing title="Keep your time for the emails only you can answer." line="Connect your mailbox and hand over one routine email today." />
    </main>
    <Footer />
  </>
);

export default ForFounders;
